
import { Header } from "@/components/layout/Header";
import { AddChildForm } from "@/components/dashboard/AddChildForm";
import { ChildProfileList } from "@/components/dashboard/ChildProfileList";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, BookOpen, Settings, Mail, Bell, Activity, BookOpenText, Share2, LogOut, GraduationCap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const Dashboard = () => {
  console.log("Dashboard component rendering");
  const [showForm, setShowForm] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleAddChildSuccess = useCallback(() => {
    setShowForm(false);
    queryClient.invalidateQueries({ queryKey: ["childProfiles"] });
    toast({
      title: "Success",
      description: "Child profile added successfully",
    });
  }, [queryClient, toast]);

  const handleShare = useCallback(async () => {
    const shareData = {
      title: "Kids Honor Roll",
      text: "Join me on Kids Honor Roll - the platform that helps track and improve your child's educational journey!",
      url: window.location.origin,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast({
          title: "Thanks for sharing!",
          description: "You're helping us grow our community.",
        });
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast({
          title: "Link copied!",
          description: "Share the link with your friends.",
        });
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  }, [toast]);

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        console.error("Error during global signout:", error);
      }
      
      toast({
        title: "Signed out successfully",
        description: "See you next time!",
      });
      
      navigate("/login");
    } catch (error) {
      console.error("Error in signout process:", error);
      navigate("/login");
    }
  }, [navigate, toast]);

  useEffect(() => {
    console.log("Dashboard mounted");
    
    const checkAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.error("No valid session found:", sessionError);
          navigate("/login");
          return;
        }
        
        console.log("Dashboard auth check passed, user:", session.user.id);
        setCheckingAuth(false);
      } catch (error) {
        console.error("Error checking auth:", error);
        navigate("/login");
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/login");
      }
    });

    return () => {
      subscription.unsubscribe();
      console.log("Dashboard unmounted");
    };
  }, [navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigationItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: GraduationCap, label: "Getting Started", path: "/getting-started" },
    { icon: BookOpen, label: "Testing Modules", path: "/testing-modules" },
    { icon: Activity, label: "Activities and Goals", path: "/activities-and-goals" },
    { icon: BookOpenText, label: "Journal", path: "/journal" },
    { icon: Bell, label: "Notifications", path: "/notifications" },
    { icon: Settings, label: "Settings", path: "/settings" },
    { icon: Mail, label: "Contact Us", path: "/contact-us" },
    { icon: Share2, label: "Tell a Friend", onClick: handleShare },
    { 
      icon: LogOut, 
      label: "Sign Out", 
      onClick: handleSignOut,
      className: "text-red-500 hover:text-red-600 hover:bg-red-50" 
    }
  ];

  // Only show loading while checking authentication, not for a fixed time period
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-600">Verifying access...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex flex-1">
        <nav className="hidden lg:block w-64 bg-white border-r border-gray-200 p-4">
          <div className="space-y-2">
            {navigationItems.map((item) => (
              <Button
                key={item.label}
                variant="ghost"
                className={`w-full justify-start gap-2 ${item.className || ''} ${
                  item.path && window.location.pathname === item.path ? "bg-gray-100" : ""
                }`}
                onClick={item.onClick || (() => navigate(item.path!))}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Button>
            ))}
          </div>
        </nav>
        <main className="flex-1 p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-2xl font-heading font-bold text-gray-900">
                  Child Profiles
                </h2>
                <Button 
                  onClick={() => setShowForm(!showForm)}
                  className="w-full sm:w-auto"
                >
                  Add New Child Profile
                </Button>
              </div>
              
              {showForm && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <AddChildForm onSuccess={handleAddChildSuccess} />
                </div>
              )}

              <ChildProfileList />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
